import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useInteractionLock } from '../../hooks/useInteractionLock';
import { useMobile } from '../../hooks/useMobile';
import { useViewportSize } from '../../hooks/useViewportSize';
import styles from './Window.module.scss';

export const Window = ({
    children, close,
    fullHeight, fullWidth,
    className, size = 'xl', mobileOrigin = 'bottom'
}: WindowProps) => {

    const { width, height } = useViewportSize();
    const isMobile = useMobile();

    const windowRef = useRef<HTMLDivElement>(null);
    const [isHidden, setIsHidden] = useState(false);

    // Lock interaction if a modal is shown above the page content.
    useInteractionLock(!isHidden);

    useEffect(() => {
        if (windowRef.current) {
            setIsHidden(isSelfOrAncestorHidden(windowRef.current));
        }
    }, [windowRef, width, height]);

    const classes = classNames(styles.container, {
        [styles.fullHeight]: fullHeight,
        [styles.fullWidth]: fullWidth,
        [styles.desktop]: !isMobile
    });

    return (
        <div className={classes} style={{ width, height }}>
            <div className={styles.backdrop} onClick={close} />
            <div className={classNames(styles.window, styles[size], styles[mobileOrigin], className)}
                ref={windowRef}>
                {children}
            </div>
        </div>
    );
};

export type WindowProps = PropsWithChildren<{
    close: () => void;
    className?: string;
    fullHeight?: boolean;
    fullWidth?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    mobileOrigin?: 'bottom' | 'center';
}>;

const isSelfOrAncestorHidden = (element: HTMLElement) => {

    let currentElement = element;

    while (currentElement.parentElement !== null) {

        if (window.getComputedStyle(currentElement).display === 'none') {
            return true;
        }

        currentElement = currentElement.parentElement;
    }

    return false;
};